<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import BotsAPI from 'dashboard/api/bots';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  stage: { type: Object, required: true },
  pipeline: { type: Object, required: true },
  bots: { type: Array, default: () => [] },
  // Lista completa de embudos (id, name, stages[]) para el picker de change_stage
  allPipelines: { type: Array, default: () => [] },
  // Edit mode: si viene `editing` con {bot, trigger}, pre-llena los campos
  editing: { type: Object, default: null },
});

const emit = defineEmits(['close', 'created', 'updated']);

const isEditMode = computed(() => !!props.editing);
const currentStep = ref(1); // 1 · Cuándo · 2 · Horario · 3 · Acción
const saving = ref(false);

// === Paso 1 · Cuándo ===
const triggerType = ref('stage_changed'); // 'stage_created' | 'stage_changed' | 'stage_any'
const delayMode = ref('immediate'); // 'immediate' | 'delayed'
// V5-P1 · 2026-05-15 (Victor) · 4 inputs inline (d/h/m/s) en lugar de input+toggle
const delayDays = ref(0);
const delayHoursInput = ref(0);
const delayMinutes = ref(0);
const delaySeconds = ref(0);

// V4 · 2026-05-14 (Victor) · Multi-etapa: stages donde aplica el disparador.
// Por default solo la stage actual (donde se abrió el wizard). Si el usuario
// selecciona más, el backend crea triggers enlazados con `linked_group_id`.
// En modo edit con linked_group_id, prefill con todas las stages del grupo.
const selectedStageIds = ref([Number(props.stage?.id)]);
function toggleStage(id) {
  const n = Number(id);
  // En modo CREATE no se puede desmarcar la stage de origen (donde se abrió wizard)
  if (!isEditMode.value && n === Number(props.stage?.id)) return;
  const idx = selectedStageIds.value.indexOf(n);
  if (idx >= 0) selectedStageIds.value.splice(idx, 1);
  else selectedStageIds.value.push(n);
}
function isStageSelected(id) { return selectedStageIds.value.includes(Number(id)); }
function isOriginStage(id) { return !isEditMode.value && Number(id) === Number(props.stage?.id); }
// Lista de stages del pipeline actual (las únicas válidas como destino del disparador).
const pipelineStages = computed(() => props.pipeline?.stages || []);

// V3 · 2026-05-14 (Victor) · Multi-sucursal: branches donde aplica este trigger.
// Vacío = aplica a todas (default). La branch activa (ORIGEN) viene siempre marcada.
const store = useStore();
const allBranches = computed(() => store.getters['branches/getBranches'] || []);
const activeBranchId = computed(() => store.getters['branches/getActiveBranchId'] || null);
// Estado: array de branch_ids seleccionadas. Default = solo la branch activa
// (= el disparador aplica donde Victor lo está creando).
const branchIds = ref([]);
function initBranchIds() {
  // En modo edit, prefill desde config.branch_ids
  if (props.editing?.trigger?.config?.branch_ids) {
    branchIds.value = Array.from(props.editing.trigger.config.branch_ids).map(Number);
    return;
  }
  // En modo create, default = branch activa (si hay)
  if (activeBranchId.value) {
    branchIds.value = [Number(activeBranchId.value)];
  } else {
    branchIds.value = [];
  }
}
function toggleBranch(id) {
  // No se puede desmarcar la branch ORIGEN (activa) en modo create
  if (!isEditMode.value && id === activeBranchId.value) return;
  const idx = branchIds.value.indexOf(id);
  if (idx >= 0) branchIds.value.splice(idx, 1);
  else branchIds.value.push(id);
}
function isBranchSelected(id) { return branchIds.value.includes(id); }
function isOriginBranch(id) { return !isEditMode.value && id === activeBranchId.value; }
function selectAllBranches() {
  branchIds.value = allBranches.value.map(b => b.id);
}
function clearBranches() {
  // Mantiene la ORIGEN si estamos en create
  if (isEditMode.value) {
    branchIds.value = [];
  } else if (activeBranchId.value) {
    branchIds.value = [Number(activeBranchId.value)];
  } else {
    branchIds.value = [];
  }
}
function branchCodeClass(b) {
  const c = (b?.code || '').toLowerCase();
  if (['pers', 'persp', 'sde', 'sdo', 'lrm', 'stg'].includes(c)) return `atw-bp-${c}`;
  return 'atw-bp-other';
}

// Pre-llenar campos en edit mode al montar
function prefillFromEditing() {
  if (!props.editing) return;
  const t = props.editing.trigger || {};
  const cfg = t.config || {};
  triggerType.value = t.trigger_type || 'stage_changed';
  // V5-P1 · prefill desde delay_seconds (nuevo) o delay_hours (legacy) → expandir a (d/h/m/s)
  let totalSeconds = Number(cfg.delay_seconds || 0);
  if (!totalSeconds && cfg.delay_hours) totalSeconds = Math.round(Number(cfg.delay_hours) * 3600);
  if (totalSeconds > 0) {
    delayMode.value = 'delayed';
    delayDays.value = Math.floor(totalSeconds / 86400);
    delayHoursInput.value = Math.floor((totalSeconds % 86400) / 3600);
    delayMinutes.value = Math.floor((totalSeconds % 3600) / 60);
    delaySeconds.value = totalSeconds % 60;
  } else {
    delayMode.value = 'immediate';
  }
  const sch = cfg.schedule || {};
  if (sch.mode === 'custom') {
    scheduleMode.value = 'custom';
    scheduleDays.value = Array.isArray(sch.days) ? [...sch.days] : ['Mo','Tu','We','Th','Fr','Sa','Su'];
    scheduleFrom.value = sch.from || '08:00';
    scheduleTo.value = sch.to || '20:00';
  } else {
    scheduleMode.value = 'always';
  }
  selectedBotId.value = props.editing.bot?.id || null;
  // Fase 3b: prefill action_type + target stage si es change_stage
  actionType.value = (cfg.action_type || 'execute_bot').toString();
  if (actionType.value === 'change_stage') {
    targetPipelineId.value = Number(cfg.target_pipeline_id) || props.pipeline?.id || null;
    targetStageId.value = Number(cfg.target_stage_id) || null;
  }
  // V4 · multi-etapa: si el trigger pertenece a un grupo enlazado, prefill con
  // TODAS las stages del grupo (no solo la actual). Permite editar el alcance.
  if (t.linked_group_id && Array.isArray(props.editing.linkedStageIds)) {
    selectedStageIds.value = props.editing.linkedStageIds.map(Number).filter(n => n > 0);
  } else if (cfg.stage_id) {
    selectedStageIds.value = [Number(cfg.stage_id)];
  }
  // Condición por etiqueta: prefill desde conditions[{type:'tags'}].
  const tagCond = Array.isArray(cfg.conditions)
    ? cfg.conditions.find(c => c && c.type === 'tags')
    : null;
  if (tagCond && Array.isArray(tagCond.values)) {
    selectedLabelIds.value = tagCond.values.map(Number).filter(n => n > 0);
  }
}

// V5-P1 · 2026-05-15 (Victor) · suma total en segundos a partir de los 4 inputs.
// delay_hours se mantiene en el payload por backwards-compat con backend antiguo,
// pero el backend nuevo prefiere delay_seconds.
const delaySecondsTotal = computed(() => {
  if (delayMode.value === 'immediate') return 0;
  return (
    Number(delayDays.value || 0) * 86400 +
    Number(delayHoursInput.value || 0) * 3600 +
    Number(delayMinutes.value || 0) * 60 +
    Number(delaySeconds.value || 0)
  );
});
const delayHours = computed(() => delaySecondsTotal.value / 3600);
const delayBreakdownLabel = computed(() => {
  const parts = [];
  if (delayDays.value) parts.push(`${delayDays.value} d`);
  if (delayHoursInput.value) parts.push(`${delayHoursInput.value} h`);
  if (delayMinutes.value) parts.push(`${delayMinutes.value} min`);
  if (delaySeconds.value) parts.push(`${delaySeconds.value} seg`);
  return parts.length ? parts.join(' · ') : '0 seg';
});

const triggerTypeOptions = [
  { value: 'stage_created', label: 'Cuando se crea en esta etapa', icon: '✨' },
  { value: 'stage_changed', label: 'Cuando es movido a esta etapa', icon: '↪' },
  { value: 'stage_any', label: 'Cuando se crea o es movido a esta etapa', icon: '⇲' },
];

// === Paso 2 · Horario ===
const scheduleMode = ref('always'); // 'always' | 'custom'
const scheduleDays = ref(['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']);
const scheduleFrom = ref('08:00');
const scheduleTo = ref('20:00');

const allDays = [
  { code: 'Mo', label: 'L' },
  { code: 'Tu', label: 'M' },
  { code: 'We', label: 'X' },
  { code: 'Th', label: 'J' },
  { code: 'Fr', label: 'V' },
  { code: 'Sa', label: 'S' },
  { code: 'Su', label: 'D' },
];

function toggleDay(code) {
  const idx = scheduleDays.value.indexOf(code);
  if (idx >= 0) scheduleDays.value.splice(idx, 1);
  else scheduleDays.value.push(code);
}

// === Paso 3 · Acción ===
const actionType = ref('execute_bot'); // 'execute_bot' | 'change_stage'
const selectedBotId = ref(null);
const botSearch = ref('');

// Para change_stage: pipeline + stage target
const targetPipelineId = ref(props.pipeline?.id || null);
const targetStageId = ref(null);

const targetPipelineOptions = computed(() => props.allPipelines || []);
const targetStageOptions = computed(() => {
  const p = (props.allPipelines || []).find(pp => pp.id === targetPipelineId.value);
  return Array.isArray(p?.stages) ? p.stages : [];
});

const filteredBots = computed(() => {
  const q = botSearch.value.trim().toLowerCase();
  if (!q) return props.bots;
  return props.bots.filter(b => (b.name || '').toLowerCase().includes(q));
});

// === Condición · Etiquetas (Victor 2026-05-25) · estilo Kommo ===
// El disparador solo aplica a leads cuya conversación tenga ALGUNA de estas
// etiquetas (lógica O). Vacío = aplica a todos. El backend ya lo evalúa vía
// Bots::ConditionsMatcher (conditions[{type:'tags'}]).
const allLabels = computed(() => store.getters['labels/getLabels'] || []);
const selectedLabelIds = ref([]);
const labelSearch = ref('');
const filteredLabels = computed(() => {
  const q = labelSearch.value.trim().toLowerCase();
  if (!q) return allLabels.value;
  return allLabels.value.filter(l => (l.title || '').toLowerCase().includes(q));
});
function toggleLabel(id) {
  const n = Number(id);
  const idx = selectedLabelIds.value.indexOf(n);
  if (idx >= 0) selectedLabelIds.value.splice(idx, 1);
  else selectedLabelIds.value.push(n);
}
function isLabelSelected(id) { return selectedLabelIds.value.includes(Number(id)); }
function labelColor(l) { return l?.color || '#c9a35a'; }

// === Validation ===
const canProceedStep1 = computed(() =>
  !!triggerType.value && (delayMode.value === 'immediate' || delaySecondsTotal.value > 0)
);
const canProceedStep2 = computed(() =>
  scheduleMode.value === 'always' ||
  (scheduleDays.value.length > 0 && scheduleFrom.value && scheduleTo.value)
);
const canSave = computed(() => {
  if (actionType.value === 'execute_bot') return !!selectedBotId.value;
  if (actionType.value === 'change_stage') return !!targetStageId.value;
  return false;
});

// === Save ===
async function save() {
  if (!canSave.value) return;
  saving.value = true;
  try {
    // V4 · multi-etapa: si el usuario seleccionó varias stages, las enviamos
    // como `stage_ids` y el backend genera N triggers enlazados (linked_group_id).
    // Stage de origen va en `stage_id` (compat backend legacy).
    const stageIds = Array.from(new Set(selectedStageIds.value.map(Number).filter(n => n > 0)));
    const primaryStageId = stageIds.includes(Number(props.stage.id))
      ? Number(props.stage.id)
      : stageIds[0];
    // Doble formato:
    // - `stage_id`: usado por Bots::TriggerDispatchService para filtrar por etapa
    // - `conditions[{type:'stage',values:[id]}]`: usado por jbuilder para computar
    //   `bot.stage_ids` y agrupar el bot en la columna correcta del modal D1
    // Condiciones: etapa (siempre) + etiquetas (opcional · lógica O · Kommo).
    const conditions = [{ type: 'stage', values: stageIds }];
    const labelIds = Array.from(new Set(selectedLabelIds.value.map(Number).filter(n => n > 0)));
    if (labelIds.length) conditions.push({ type: 'tags', values: labelIds });
    const config = {
      stage_id: primaryStageId,
      stage_ids: stageIds,
      conditions,
      delay_hours: delayHours.value,
      delay_seconds: delaySecondsTotal.value,
      action_type: actionType.value,
      // V3 · 2026-05-14 (Victor) · multi-sucursal: si hay branch_ids, solo se
      // dispara cuando conv.branch_id está en la lista. Vacío = todas las sucursales.
      branch_ids: Array.from(branchIds.value).map(Number).filter(n => n > 0),
    };
    if (actionType.value === 'change_stage') {
      config.target_pipeline_id = Number(targetPipelineId.value);
      config.target_stage_id = Number(targetStageId.value);
    }
    if (scheduleMode.value === 'custom') {
      config.schedule = {
        mode: 'custom',
        days: scheduleDays.value,
        from: scheduleFrom.value,
        to: scheduleTo.value,
      };
    } else {
      config.schedule = { mode: 'always' };
    }

    // Resolver bot_id: para change_stage usamos el primer bot del account como host
    // (el trigger requiere bot_id en DB pero el bot no se ejecuta — el dispatcher
    //  corta antes al detectar action_type=change_stage).
    let botIdToUse = selectedBotId.value;
    if (actionType.value === 'change_stage') {
      botIdToUse = (props.editing?.bot?.id) || (props.bots?.[0]?.id);
    }

    if (!botIdToUse) {
      useAlert('Crea al menos un Salesbot primero (Ajustes → Salesbots)');
      saving.value = false;
      return;
    }

    if (isEditMode.value && props.editing?.trigger?.id && props.editing?.bot?.id) {
      // V4 · Multi-etapa edit logic:
      // - Trigger enlazado + stages NO cambiaron → preguntar propagate (sí/no)
      // - Trigger enlazado + stages SÍ cambiaron → propagate=true forzado
      //   (backend hace add/remove/update transaccional)
      // - Trigger NO enlazado → update simple
      const isLinked = !!props.editing.trigger.linked_group_id;
      const originalStageIds = (props.editing.linkedStageIds || [Number(props.editing.trigger.config?.stage_id)])
        .map(Number).filter(n => n > 0).sort();
      const newSorted = [...stageIds].sort();
      const stagesChanged = isLinked &&
        (newSorted.length !== originalStageIds.length ||
         newSorted.some((s, i) => s !== originalStageIds[i]));

      let propagate = true;
      if (isLinked && !stagesChanged) {
        const total = props.editing.linkedCount || originalStageIds.length;
        const others = Math.max(0, total - 1);
        propagate = window.confirm(
          `Este disparador está enlazado con ${others} disparador(es) más.\n\n` +
          `¿Aplicar este cambio a TODOS los del grupo?\n\n` +
          `OK = aplicar a todos\n` +
          `Cancelar = aplicar solo a este (lo desvincula del grupo)`
        );
      }

      // V5.2 · 2026-05-14 (Victor) · bot_id editable en edit mode.
      // El backend mueve el trigger al nuevo bot si bot_id cambió.
      const finalBotId = actionType.value === 'execute_bot' && selectedBotId.value
        ? selectedBotId.value
        : props.editing.bot.id;
      await BotsAPI.updateTrigger(props.editing.bot.id, props.editing.trigger.id, {
        bot_id: finalBotId,
        trigger_type: triggerType.value,
        active: true,
        config,
        propagate_to_linked: propagate,
      });
      useAlert('Disparador actualizado');
      emit('updated');
    } else {
      await BotsAPI.createTrigger(botIdToUse, {
        trigger_type: triggerType.value,
        active: true,
        config,
      });
      useAlert(actionType.value === 'change_stage' ? 'Disparador de cambio de etapa creado' : 'Disparador creado · se ejecutará según delay/horario');
      emit('created');
    }
    close();
  } catch (e) {
    useAlert(`Error: ${e.response?.data?.message || e.message}`);
  } finally {
    saving.value = false;
  }
}

function close() { emit('close'); }
function nextStep() { if (currentStep.value < 3) currentStep.value++; }
function prevStep() { if (currentStep.value > 1) currentStep.value--; }

// Esc para cerrar
function onKeydown(e) {
  if (e.key === 'Escape') close();
}
onMounted(() => window.addEventListener('keydown', onKeydown));
watch(() => null, () => {}, { onCleanup: () => window.removeEventListener('keydown', onKeydown) });

onMounted(() => {
  prefillFromEditing();
  initBranchIds();
  if (!allLabels.value.length) store.dispatch('labels/get');
});
</script>

<template>
  <div class="atw-overlay" @click.self="close">
    <div class="atw-modal">

      <!-- Header -->
      <div class="atw-header">
        <div class="atw-title">
          <span class="atw-title-icon">⚡</span>
          <div>
            <div class="atw-title-name">{{ isEditMode ? 'Editar disparador' : 'Nuevo disparador' }}</div>
            <div class="atw-title-stage">Etapa: <b>{{ stage.name }}</b></div>
          </div>
        </div>
        <button class="atw-close" @click="close" title="Cerrar (Esc)">✕</button>
      </div>

      <!-- Stepper -->
      <div class="atw-stepper">
        <div class="atw-step" :class="{ active: currentStep === 1, done: currentStep > 1 }">
          <span class="atw-step-num">1</span>
          <span>Cuándo</span>
        </div>
        <div class="atw-step-bar" :class="{ active: currentStep >= 2 }"></div>
        <div class="atw-step" :class="{ active: currentStep === 2, done: currentStep > 2 }">
          <span class="atw-step-num">2</span>
          <span>Horario</span>
        </div>
        <div class="atw-step-bar" :class="{ active: currentStep >= 3 }"></div>
        <div class="atw-step" :class="{ active: currentStep === 3 }">
          <span class="atw-step-num">3</span>
          <span>Acción</span>
        </div>
      </div>

      <!-- Body -->
      <div class="atw-body">

        <!-- Step 1 · Cuándo -->
        <div v-if="currentStep === 1" class="atw-pane">
          <div class="atw-field-label">Tipo de evento</div>
          <div class="atw-options">
            <button
              v-for="opt in triggerTypeOptions"
              :key="opt.value"
              class="atw-option"
              :class="{ active: triggerType === opt.value }"
              @click="triggerType = opt.value"
            >
              <span class="atw-option-icon">{{ opt.icon }}</span>
              <span>{{ opt.label }}</span>
            </button>
          </div>

          <!-- V3 · 2026-05-14 (Victor) · Multi-sucursal -->
          <div class="atw-branch-section" v-if="allBranches.length > 0">
            <div class="atw-branch-head">
              <span class="atw-branch-head-title">🏢 Sucursales donde aplica</span>
              <span class="atw-branch-head-sub">El disparador solo se ejecuta para conversaciones de las sucursales marcadas · si vacío = todas</span>
            </div>
            <div class="atw-branch-actions">
              <button type="button" class="atw-branch-quick" @click="selectAllBranches">✓ Seleccionar todas</button>
              <button type="button" class="atw-branch-quick atw-branch-quick-clear" @click="clearBranches">✕ Limpiar</button>
            </div>
            <div class="atw-branch-grid">
              <button
                v-for="b in allBranches"
                :key="`tbr-${b.id}`"
                type="button"
                class="atw-branch-pill"
                :class="{
                  'is-selected': isBranchSelected(b.id),
                  'is-origin': isOriginBranch(b.id)
                }"
                :disabled="isOriginBranch(b.id)"
                :title="isOriginBranch(b.id) ? 'Sucursal origen · siempre incluida' : (isBranchSelected(b.id) ? 'Click para desmarcar' : 'Click para marcar')"
                @click="toggleBranch(b.id)"
              >
                <span class="atw-bp-check">
                  <span v-if="isBranchSelected(b.id)">✓</span>
                </span>
                <span class="atw-bp-tile" :class="branchCodeClass(b)">{{ b.code || b.name?.substring(0, 4).toUpperCase() }}</span>
                <span class="atw-bp-name">{{ b.name }}</span>
                <span v-if="isOriginBranch(b.id)" class="atw-bp-origin">ORIGEN</span>
              </button>
            </div>
            <div class="atw-branch-summary">
              <template v-if="branchIds.length === 0">
                ⚠ Sin sucursales marcadas → este disparador aplicará a <strong>TODAS</strong> las sucursales.
              </template>
              <template v-else>
                ✅ Aplicará en <strong>{{ branchIds.length }} sucursal{{ branchIds.length === 1 ? '' : 'es' }}</strong>: {{ allBranches.filter(b => branchIds.includes(b.id)).map(b => b.code).join(', ') }}
              </template>
            </div>
          </div>

          <!-- V4 · 2026-05-14 (Victor) · Multi-etapa: aplicar disparador a varias etapas -->
          <div class="atw-stages-section" v-if="pipelineStages.length > 1">
            <div class="atw-branch-head">
              <span class="atw-branch-head-title">⛓ Etapas donde aplica</span>
              <span class="atw-branch-head-sub">El disparador se crea por cada etapa marcada · todos quedan enlazados · editar uno te pregunta si propagar al grupo</span>
            </div>
            <div class="atw-branch-grid">
              <button
                v-for="st in pipelineStages"
                :key="`tst-${st.id}`"
                type="button"
                class="atw-branch-pill"
                :class="{
                  'is-selected': isStageSelected(st.id),
                  'is-origin': isOriginStage(st.id)
                }"
                :disabled="isOriginStage(st.id)"
                :title="isOriginStage(st.id) ? 'Etapa origen · siempre incluida' : (isStageSelected(st.id) ? 'Click para desmarcar' : 'Click para marcar')"
                @click="toggleStage(st.id)"
              >
                <span class="atw-bp-check">
                  <span v-if="isStageSelected(st.id)">✓</span>
                </span>
                <span class="atw-bp-tile" :style="{background: st.color || '#fbbf24', color: '#000'}">{{ (st.name || '').substring(0, 3).toUpperCase() }}</span>
                <span class="atw-bp-name">{{ st.name }}</span>
                <span v-if="isOriginStage(st.id)" class="atw-bp-origin">ORIGEN</span>
              </button>
            </div>
            <div class="atw-branch-summary">
              <template v-if="selectedStageIds.length <= 1">
                ✅ Se creará <strong>1 disparador</strong> en esta etapa.
              </template>
              <template v-else>
                ⛓ Se crearán <strong>{{ selectedStageIds.length }} disparadores enlazados</strong> (un grupo). Al editar uno te preguntará si propagar al resto.
              </template>
            </div>
          </div>

          <div class="atw-field-label" style="margin-top: 18px">Retraso (delay)</div>
          <div class="atw-radio-row">
            <button
              class="atw-radio"
              :class="{ active: delayMode === 'immediate' }"
              @click="delayMode = 'immediate'"
            >
              <span class="atw-radio-dot"></span>
              <span>Inmediato</span>
            </button>
            <button
              class="atw-radio"
              :class="{ active: delayMode === 'delayed' }"
              @click="delayMode = 'delayed'"
            >
              <span class="atw-radio-dot"></span>
              <span>Después de…</span>
            </button>
          </div>
          <div v-if="delayMode === 'delayed'" class="atw-delay-grid">
            <div class="atw-delay-field">
              <div class="atw-delay-field-label">Días</div>
              <input v-model.number="delayDays" type="number" min="0" class="atw-delay-input" placeholder="0" />
              <div class="atw-delay-field-suffix">d</div>
            </div>
            <div class="atw-delay-field">
              <div class="atw-delay-field-label">Horas</div>
              <input v-model.number="delayHoursInput" type="number" min="0" max="23" class="atw-delay-input" placeholder="0" />
              <div class="atw-delay-field-suffix">h</div>
            </div>
            <div class="atw-delay-field">
              <div class="atw-delay-field-label">Minutos</div>
              <input v-model.number="delayMinutes" type="number" min="0" max="59" class="atw-delay-input" placeholder="0" />
              <div class="atw-delay-field-suffix">m</div>
            </div>
            <div class="atw-delay-field">
              <div class="atw-delay-field-label">Segundos</div>
              <input v-model.number="delaySeconds" type="number" min="0" max="59" class="atw-delay-input" placeholder="0" />
              <div class="atw-delay-field-suffix">s</div>
            </div>
          </div>
          <div v-if="delayMode === 'delayed'" class="atw-delay-total">
            <span>⏱ Total:</span>
            <b>{{ delayBreakdownLabel }}</b>
          </div>
        </div>

        <!-- Step 2 · Horario -->
        <div v-else-if="currentStep === 2" class="atw-pane">
          <div class="atw-field-label">Cuándo puede dispararse</div>
          <div class="atw-radio-row">
            <button
              class="atw-radio"
              :class="{ active: scheduleMode === 'always' }"
              @click="scheduleMode = 'always'"
            >
              <span class="atw-radio-dot"></span>
              <span>Siempre · 24/7</span>
            </button>
            <button
              class="atw-radio"
              :class="{ active: scheduleMode === 'custom' }"
              @click="scheduleMode = 'custom'"
            >
              <span class="atw-radio-dot"></span>
              <span>Días y horas específicos</span>
            </button>
          </div>

          <div v-if="scheduleMode === 'custom'" style="margin-top: 18px">
            <div class="atw-field-label">Días</div>
            <div class="atw-days">
              <button
                v-for="d in allDays"
                :key="d.code"
                class="atw-day"
                :class="{ active: scheduleDays.includes(d.code) }"
                @click="toggleDay(d.code)"
              >{{ d.label }}</button>
            </div>

            <div class="atw-field-label" style="margin-top: 14px">Ventana horaria</div>
            <div class="atw-time-row">
              <input v-model="scheduleFrom" type="time" class="atw-input atw-input-time" />
              <span class="atw-time-sep">hasta</span>
              <input v-model="scheduleTo" type="time" class="atw-input atw-input-time" />
            </div>
          </div>
        </div>

        <!-- Step 3 · Acción -->
        <div v-else-if="currentStep === 3" class="atw-pane">
          <div class="atw-field-label">Tipo de acción</div>
          <div class="atw-options">
            <button
              class="atw-option"
              :class="{ active: actionType === 'execute_bot' }"
              @click="actionType = 'execute_bot'"
            >
              <span class="atw-option-icon">🤖</span>
              <span>Ejecutar Salesbot</span>
            </button>
            <button
              class="atw-option"
              :class="{ active: actionType === 'change_stage' }"
              @click="actionType = 'change_stage'"
            >
              <span class="atw-option-icon">↻</span>
              <span>Cambiar etapa principal</span>
            </button>
          </div>

          <div v-if="actionType === 'change_stage'" style="margin-top: 18px">
            <div class="atw-field-label">Embudo destino</div>
            <select v-model.number="targetPipelineId" class="atw-input atw-input-select">
              <option v-for="p in targetPipelineOptions" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>

            <div class="atw-field-label" style="margin-top: 14px">Etapa destino</div>
            <select v-model.number="targetStageId" class="atw-input atw-input-select">
              <option :value="null" disabled>— Elegir etapa —</option>
              <option v-for="s in targetStageOptions" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>

            <div v-if="targetStageId && targetStageId === stage.id" class="atw-warn">
              ⚠ Etapa destino igual a la actual · el disparador no haría nada
            </div>
          </div>

          <div v-if="actionType === 'execute_bot'" style="margin-top: 18px;">
            <div class="atw-field-label">
              {{ isEditMode ? 'Salesbot (editable)' : 'Elegir Salesbot' }}
            </div>
            <div class="atw-search">
              <span class="atw-search-icon">🔍</span>
              <input
                v-model="botSearch"
                type="text"
                placeholder="Buscar bot por nombre…"
              />
            </div>
            <div class="atw-bot-list">
              <button
                v-for="b in filteredBots"
                :key="b.id"
                class="atw-bot-item"
                :class="{ active: selectedBotId === b.id }"
                @click="selectedBotId = b.id"
              >
                <span class="atw-bot-radio">
                  <span v-if="selectedBotId === b.id"></span>
                </span>
                <span class="atw-bot-icon">🤖</span>
                <span class="atw-bot-info">
                  <span class="atw-bot-name">{{ b.name }}</span>
                  <span class="atw-bot-meta">
                    <template v-if="b.category">{{ b.category }} · </template>
                    {{ b.trigger_summary || 'Sin trigger' }}
                  </span>
                </span>
              </button>
              <div v-if="filteredBots.length === 0" class="atw-bot-empty">
                Sin bots que coincidan · crea uno en <a href="#" style="color:#fbbf24">Ajustes → Salesbots</a>
              </div>
            </div>
          </div>

          <!-- Condición · Etiquetas (Victor 2026-05-25) · estilo Kommo -->
          <div class="atw-field-label" style="margin-top: 20px">
            🏷️ Solo leads con etiqueta <span style="color:#5a5d6b;font-weight:600">· opcional</span>
          </div>
          <div class="atw-cond-hint">
            Si eliges una o más etiquetas, el disparador <b>solo aplica a los leads que tengan alguna de ellas</b>. Vacío = aplica a todos los leads de la etapa.
          </div>
          <div v-if="allLabels.length > 8" class="atw-search" style="margin-bottom:10px">
            <span class="atw-search-icon">🔍</span>
            <input v-model="labelSearch" type="text" placeholder="Buscar etiqueta…" />
          </div>
          <div class="atw-chips">
            <button
              v-for="l in filteredLabels"
              :key="l.id"
              class="atw-chip"
              :class="{ on: isLabelSelected(l.id) }"
              @click="toggleLabel(l.id)"
            >
              <span class="atw-chip-dot" :style="{ background: labelColor(l) }"></span>
              <span v-if="isLabelSelected(l.id)" class="atw-chip-ck">✓</span>
              {{ l.title }}
            </button>
            <div v-if="!allLabels.length" class="atw-bot-empty">
              No hay etiquetas creadas en esta cuenta.
            </div>
          </div>
          <div v-if="selectedLabelIds.length" class="atw-cond-note">
            ✓ Coincide si el lead tiene <b>cualquiera</b> de las {{ selectedLabelIds.length }} etiqueta(s) elegida(s).
          </div>
        </div>

      </div>

      <!-- Footer -->
      <div class="atw-footer">
        <button class="atw-footer-cancel" @click="close">Cancelar</button>
        <div style="flex:1"></div>
        <button v-if="currentStep > 1" class="atw-footer-back" @click="prevStep">← Atrás</button>
        <button
          v-if="currentStep < 3"
          class="atw-footer-next"
          :disabled="(currentStep === 1 && !canProceedStep1) || (currentStep === 2 && !canProceedStep2)"
          @click="nextStep"
        >Siguiente →</button>
        <button
          v-else
          class="atw-footer-save"
          :disabled="!canSave || saving"
          @click="save"
        >
          {{ saving ? 'Guardando…' : (isEditMode ? '💾 Guardar cambios' : '💾 Crear disparador') }}
        </button>
      </div>

    </div>
  </div>
</template>

<style scoped>
.atw-overlay {
  position: fixed; inset: 0;
  z-index: 100;
  background: rgba(0,0,0,0.65);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.atw-modal {
  width: 100%;
  max-width: 540px;
  background: #0a0a0a;
  border: 1px solid rgba(251,191,36,0.25);
  border-radius: 14px;
  box-shadow: 0 24px 70px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 30px rgba(251,191,36,0.10);
  display: flex; flex-direction: column;
  max-height: 92vh;
  overflow: hidden;
  position: relative;
}
.atw-modal::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(120deg, transparent 38%, rgba(255,255,255,0.04) 50%, transparent 52%),
    radial-gradient(circle at 100% 0%, rgba(251,191,36,0.08), transparent 60%);
  border-radius: 14px;
}
.atw-modal > * { position: relative; z-index: 1; }

/* Header */
.atw-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.atw-title { display: flex; align-items: center; gap: 12px; }
.atw-title-icon {
  width: 36px; height: 36px;
  border-radius: 9px;
  background: linear-gradient(135deg, rgba(251,191,36,0.25), rgba(217,119,6,0.10));
  border: 1px solid rgba(251,191,36,0.40);
  color: #fbbf24;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 16px;
  box-shadow: 0 0 12px rgba(251,191,36,0.30);
}
.atw-title-name { font-size: 14px; font-weight: 900; color: #fff; }
.atw-title-stage { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 2px; }
.atw-title-stage b { color: #fbbf24; }
.atw-close {
  width: 32px; height: 32px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 8px;
  color: rgba(255,255,255,0.55);
  cursor: pointer;
  font-size: 13px;
  font-family: inherit;
}
.atw-close:hover { background: rgba(255,255,255,0.05); color: #fff; }

/* Stepper */
.atw-stepper {
  display: flex; align-items: center; gap: 6px;
  padding: 12px 20px;
  background: rgba(255,255,255,0.02);
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.atw-step {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px;
  color: rgba(255,255,255,0.50);
  font-weight: 700;
}
.atw-step-num {
  width: 22px; height: 22px;
  border-radius: 50%;
  background: rgba(255,255,255,0.05);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 10.5px; font-weight: 900;
  color: rgba(255,255,255,0.45);
  border: 1px solid rgba(255,255,255,0.08);
}
.atw-step.active { color: #fff; }
.atw-step.active .atw-step-num {
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1a1209;
  border-color: rgba(251,191,36,0.45);
  box-shadow: 0 0 10px rgba(251,191,36,0.40);
}
.atw-step.done .atw-step-num {
  background: rgba(74,222,128,0.15);
  border-color: rgba(74,222,128,0.35);
  color: #4ade80;
}
.atw-step.done .atw-step-num::before { content: '✓'; font-size: 11px; }
.atw-step.done .atw-step-num > * { display: none; }
.atw-step-bar {
  flex: 1;
  height: 1px;
  background: rgba(255,255,255,0.06);
}
.atw-step-bar.active { background: rgba(251,191,36,0.40); }

/* Body */
.atw-body {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  min-height: 280px;
}
.atw-pane { animation: fadeIn 0.2s ease; }
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.atw-field-label {
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255,255,255,0.55);
  margin-bottom: 8px;
}

.atw-options {
  display: flex; flex-direction: column;
  gap: 6px;
}
.atw-option {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 9px;
  color: rgba(255,255,255,0.80);
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: all 0.15s;
}
.atw-option:hover:not(.atw-option-disabled) {
  border-color: rgba(251,191,36,0.30);
  background: rgba(255,255,255,0.05);
}
.atw-option.active {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.50);
  color: #fff;
  box-shadow: 0 0 0 1px rgba(251,191,36,0.10) inset;
}
.atw-option-icon {
  width: 24px; height: 24px;
  border-radius: 6px;
  background: rgba(255,255,255,0.06);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}
.atw-option.active .atw-option-icon {
  background: rgba(251,191,36,0.20);
  color: #fbbf24;
}
.atw-option-disabled { opacity: 0.40; cursor: not-allowed; }
.atw-soon {
  font-size: 9px; font-weight: 800;
  padding: 1px 5px;
  margin-left: 4px;
  background: rgba(251,191,36,0.15);
  color: #fbbf24;
  border-radius: 999px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

/* Radios */
.atw-radio-row {
  display: flex; gap: 6px;
}
.atw-radio {
  flex: 1;
  display: flex; align-items: center; gap: 8px;
  padding: 9px 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: rgba(255,255,255,0.70);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.atw-radio:hover { border-color: rgba(251,191,36,0.30); }
.atw-radio.active {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.50);
  color: #fff;
}
.atw-radio-dot {
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 1.5px solid rgba(255,255,255,0.30);
  position: relative;
  flex-shrink: 0;
}
.atw-radio.active .atw-radio-dot {
  border-color: #fbbf24;
}
.atw-radio.active .atw-radio-dot::after {
  content: '';
  position: absolute;
  inset: 2px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 0 6px rgba(251,191,36,0.50);
}

/* V5-P1 · 2026-05-15 (Victor) · Grid de 4 inputs (d/h/m/s) para el delay */
.atw-delay-grid {
  display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap;
  margin-top: 10px;
}
.atw-delay-field {
  display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 90px;
}
.atw-delay-field-label {
  font-size: 10px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase;
  color: rgba(255,255,255,0.50);
}
.atw-delay-input {
  background: rgba(0,0,0,0.40); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px; padding: 13px 12px;
  font-size: 22px; font-weight: 700; text-align: center;
  color: #fff; outline: none; transition: border-color 0.15s;
  font-variant-numeric: tabular-nums; font-family: inherit;
}
.atw-delay-input:focus { border-color: rgba(251,191,36,0.55); }
.atw-delay-input[type=number]::-webkit-inner-spin-button,
.atw-delay-input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none; margin: 0;
}
.atw-delay-field-suffix {
  font-size: 10px; font-weight: 700; color: rgba(251,191,36,0.70);
  text-align: center; letter-spacing: 0.5px;
}
.atw-delay-total {
  margin-top: 12px; padding: 9px 12px; border-radius: 7px;
  background: rgba(251,191,36,0.06); border: 1px solid rgba(251,191,36,0.25);
  font-size: 11px; color: #fde68a;
  display: flex; align-items: center; gap: 6px;
}
.atw-delay-total b { color: #fff; font-weight: 700; }

.atw-input {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  padding: 8px 12px;
  outline: 0;
  font-family: inherit;
}
.atw-input:focus { border-color: rgba(251,191,36,0.45); }
.atw-input-select {
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, rgba(255,255,255,0.5) 50%),
                    linear-gradient(135deg, rgba(255,255,255,0.5) 50%, transparent 50%);
  background-position: calc(100% - 16px) 50%, calc(100% - 11px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 36px;
  cursor: pointer;
  font-weight: 700;
}
.atw-input-select option { background: #0a0a0a; color: #fff; }
.atw-warn {
  margin-top: 12px;
  padding: 8px 12px;
  background: rgba(239,68,68,0.10);
  border: 1px solid rgba(239,68,68,0.30);
  border-radius: 8px;
  font-size: 11px;
  color: #fca5a5;
}

/* Días */
.atw-days {
  display: flex; gap: 5px;
}
.atw-day {
  width: 36px; height: 36px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: rgba(255,255,255,0.65);
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  font-family: inherit;
}
.atw-day.active {
  background: rgba(251,191,36,0.18);
  border-color: rgba(251,191,36,0.50);
  color: #fbbf24;
  box-shadow: 0 0 8px rgba(251,191,36,0.25);
}

.atw-time-row {
  display: flex; align-items: center; gap: 8px;
}
.atw-input-time {
  font-weight: 700;
  letter-spacing: 0.5px;
}
.atw-time-sep {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
}

/* Search + bot list */
.atw-search {
  display: flex; align-items: center; gap: 8px;
  padding: 0 12px;
  height: 34px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 9px;
  margin-bottom: 8px;
}
.atw-search input {
  flex: 1; background: transparent; border: 0; outline: 0;
  color: #fff; font-size: 12.5px;
  font-family: inherit;
}
.atw-search input::placeholder { color: rgba(255,255,255,0.40); }
.atw-search-icon { font-size: 12px; color: rgba(255,255,255,0.45); }

.atw-bot-list {
  display: flex; flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
}
.atw-bot-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px;
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  color: rgba(255,255,255,0.85);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: all 0.15s;
}
.atw-bot-item:hover { border-color: rgba(251,191,36,0.30); }
.atw-bot-item.active {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.50);
}
.atw-bot-radio {
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 1.5px solid rgba(255,255,255,0.30);
  position: relative;
  flex-shrink: 0;
}
.atw-bot-item.active .atw-bot-radio {
  border-color: #fbbf24;
}
.atw-bot-item.active .atw-bot-radio::after {
  content: '';
  position: absolute;
  inset: 2px;
  border-radius: 50%;
  background: #fbbf24;
}
.atw-bot-icon {
  width: 24px; height: 24px;
  border-radius: 6px;
  background: rgba(96,165,250,0.20);
  color: #60a5fa;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}
.atw-bot-info { flex: 1; min-width: 0; line-height: 1.25; }
.atw-bot-name { display: block; font-size: 12px; font-weight: 800; color: #fff; }
.atw-bot-meta { display: block; font-size: 10px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.atw-bot-empty {
  padding: 16px;
  text-align: center;
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  border: 1px dashed rgba(255,255,255,0.10);
  border-radius: 8px;
}

/* Condición · Etiquetas (Victor 2026-05-25) · estilo Kommo */
.atw-cond-hint {
  font-size: 11.5px;
  color: rgba(255,255,255,0.55);
  line-height: 1.5;
  margin-bottom: 12px;
}
.atw-cond-hint b { color: rgba(255,255,255,0.85); }
.atw-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.atw-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  color: #b9bccb;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  transition: all 0.15s ease;
}
.atw-chip:hover { border-color: rgba(201,163,90,0.4); }
.atw-chip.on {
  color: #1a1407;
  background: linear-gradient(135deg,#e3c98a,#c9a35a);
  border-color: transparent;
  box-shadow: 0 0 14px rgba(201,163,90,0.4);
}
.atw-chip-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.atw-chip.on .atw-chip-dot { display: none; }
.atw-chip-ck { font-size: 11px; }
.atw-cond-note {
  margin-top: 11px;
  font-size: 11.5px;
  color: #c9a35a;
  font-weight: 600;
}
.atw-cond-note b { color: #e3c98a; }

/* Footer */
.atw-footer {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
}
.atw-footer-cancel,
.atw-footer-back {
  padding: 8px 14px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  color: rgba(255,255,255,0.70);
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.atw-footer-cancel:hover,
.atw-footer-back:hover { background: rgba(255,255,255,0.04); color: #fff; }
.atw-footer-next,
.atw-footer-save {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1a1209;
  border: 0;
  border-radius: 9px;
  font-size: 12px; font-weight: 800;
  cursor: pointer;
  letter-spacing: 0.3px;
  box-shadow: 0 3px 12px rgba(251,191,36,0.40);
  font-family: inherit;
}
/* V3 · 2026-05-14 (Victor) · Multi-sucursal · selector de branches */
.atw-branch-section {
  margin-top: 18px;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(40,180,90,0.10), rgba(20,40,30,0.45));
  border: 1.5px solid rgba(40,180,90,0.45);
  border-radius: 12px;
  position: relative;
}
.atw-branch-section::before {
  content: '🆕';
  position: absolute;
  top: -8px;
  right: 12px;
  background: linear-gradient(135deg, #4ade80, #16a34a);
  color: #0a0807;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
}
.atw-branch-head { margin-bottom: 10px; }
.atw-branch-head-title { display: block; color: #6cd497; font-size: 12px; font-weight: 800; letter-spacing: 0.4px; }
.atw-branch-head-sub { display: block; color: rgba(255,255,255,0.55); font-size: 10.5px; margin-top: 3px; line-height: 1.5; }
.atw-branch-actions { display: flex; gap: 8px; margin-bottom: 10px; }
.atw-branch-quick {
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(196,150,60,0.10);
  border: 1px solid rgba(196,150,60,0.25);
  color: #c8b78f;
  font-size: 10.5px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}
.atw-branch-quick:hover { background: rgba(196,150,60,0.18); color: #f4d57a; }
.atw-branch-quick-clear { background: rgba(244,63,94,0.08); border-color: rgba(244,63,94,0.30); color: #fda4af; }
.atw-branch-quick-clear:hover { background: rgba(244,63,94,0.15); color: #ff5d70; }

.atw-branch-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
}
.atw-branch-pill {
  display: flex; align-items: center; gap: 8px;
  background: rgba(20,15,10,0.55);
  border: 1.5px solid rgba(196,150,60,0.18);
  border-radius: 8px;
  padding: 7px 10px;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  text-align: left;
}
.atw-branch-pill.is-selected {
  background: linear-gradient(135deg, rgba(40,180,90,0.12), rgba(20,40,30,0.4));
  border-color: rgba(40,180,90,0.55);
}
.atw-branch-pill.is-origin {
  background: linear-gradient(135deg, rgba(196,150,60,0.18), rgba(40,30,18,0.5));
  border-color: #f4d57a;
  box-shadow: 0 0 10px rgba(196,150,60,0.25);
  cursor: default;
}
.atw-bp-check {
  width: 16px; height: 16px;
  border-radius: 4px;
  border: 1.5px solid rgba(196,150,60,0.4);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 800;
  color: transparent;
}
.atw-branch-pill.is-selected .atw-bp-check {
  background: linear-gradient(135deg, #4ade80, #16a34a);
  border-color: #4ade80;
  color: #0a0807;
}
.atw-branch-pill.is-origin .atw-bp-check {
  background: linear-gradient(135deg, #f4d57a, #c89b3f);
  border-color: #f4d57a;
  color: #1a1410;
}
.atw-bp-tile {
  width: 26px; height: 26px;
  border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  font-size: 9px;
  font-weight: 800;
  flex-shrink: 0;
  border: 1px solid;
}
.atw-bp-pers { background: rgba(124,58,237,0.15); color: #c4b5fd; border-color: rgba(124,58,237,0.45); }
.atw-bp-persp { background: rgba(245,158,11,0.18); color: #fbbf24; border-color: rgba(245,158,11,0.5); }
.atw-bp-sde { background: rgba(40,180,90,0.15); color: #6cd497; border-color: rgba(40,180,90,0.45); }
.atw-bp-sdo { background: rgba(245,158,11,0.15); color: #fbbf24; border-color: rgba(245,158,11,0.45); }
.atw-bp-lrm { background: rgba(168,85,247,0.15); color: #d8b4fe; border-color: rgba(168,85,247,0.45); }
.atw-bp-stg { background: rgba(244,63,94,0.15); color: #fda4af; border-color: rgba(244,63,94,0.45); }
.atw-bp-other { background: rgba(196,150,60,0.15); color: #f4d57a; border-color: rgba(196,150,60,0.4); }
.atw-bp-name { flex: 1; color: rgba(255,255,255,0.85); font-size: 11px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.atw-bp-origin {
  background: rgba(196,150,60,0.25);
  color: #f4d57a;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.3px;
}
.atw-branch-summary {
  margin-top: 10px;
  padding: 7px 10px;
  background: rgba(40,180,90,0.06);
  border: 1px dashed rgba(40,180,90,0.35);
  border-radius: 7px;
  color: #6cd497;
  font-size: 10.5px;
  line-height: 1.5;
}
.atw-branch-summary strong { color: #6cd497; font-weight: 800; }

.atw-footer-next:disabled,
.atw-footer-save:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
